// Registry of every customisable dropdown list — event types, designations,
// expense heads, ... — with the values a fresh parish starts from.
//
// The lists themselves live on the server in ChurchConfig.optionLists (one
// JSON map, one entry per key) so every user sees the same choices. Modules
// read and write them through useOptionList / SearchableSelect; the Settings
// screen edits them all in one place. Defaults sit here rather than in each
// module so Settings can show a list before anyone has customised it.
//
// `module` is the MODULES id the list belongs to, for grouping on Settings.

export interface OptionListDefinition {
  key: string;
  label: string;
  /** MODULES[].id, or 'Settings' for the lists the admin screens use. */
  module: string;
  defaults: readonly string[];
}

export const OPTION_LISTS: readonly OptionListDefinition[] = [
  // Congregation
  // "Guest" is more than a label: the member form and the Guests tab switch on
  // it (features/members/guest). Removing it from the list does not stop that.
  { key: 'church_member_statuses', module: 'Members & Families', label: 'Member statuses',
    defaults: ['Regular', 'Associate', 'NRI', 'Visitor', 'Guest'] },

  // Pastoral
  { key: 'church_attendance_events', module: 'Attendance', label: 'Service / event names',
    defaults: ['Sunday Morning Service', 'Sunday Evening Service', 'Youth Fellowship',
      'Women’s Fellowship', 'Men’s Fellowship', 'Friday Fasting Prayer', 'Special Meeting'] },
  { key: 'church_event_types', module: 'Event Management', label: 'Event types (also the calendar quick-add)',
    defaults: ['Sunday Mass', 'Wedding', 'Baptism', 'Bible Study', 'Choir Practice', 'Youth Meeting', 'Committee Board'] },
  { key: 'church_pc_pastors', module: 'Pastoral Care', label: 'Pastors',
    defaults: ['Senior Pastor', 'Associate Pastor', 'Youth Pastor'] },
  { key: 'church_pc_visit_purposes', module: 'Pastoral Care', label: 'Visit purposes',
    defaults: ['Routine Check-in', 'House Blessing', 'Hospital Follow-up', 'Bereavement Visit'] },
  { key: 'church_pc_couns_types', module: 'Pastoral Care', label: 'Counseling types',
    defaults: ['Marriage Counseling', 'Youth Counseling', 'Family Counseling', 'Grief Support', 'General Pastoral Guidance'] },
  { key: 'church_pc_couns_statuses', module: 'Pastoral Care', label: 'Counseling statuses',
    defaults: ['Ongoing', 'Resolved / Concluded', 'Referred to Professional'] },

  // Finance
  { key: 'church_donation_purposes', module: 'Offerings', label: 'Donation purposes',
    defaults: ['Sunday Offering', 'Weekly Offering', 'Building Fund', 'Mission Fund',
      'Charity Fund', 'Education Support', 'UPI', 'Bank Transfer', 'Payment Gateway'] },
  { key: 'church_income_sources', module: 'Offerings', label: 'Collection sources',
    defaults: ['Sunday Collection', 'Special Service Collection', 'Thanksgiving Offering', 'Harvest Collection'] },
  { key: 'church_personnel_list', module: 'Offerings', label: 'Counting / verifying personnel',
    defaults: ['Admin', 'Head Cashier'] },
  { key: 'church_bgt_fy', module: 'Budget Management', label: 'Financial years',
    defaults: ['2026-2027', '2025-2026'] },
  { key: 'church_bgt_depts', module: 'Budget Management', label: 'Budget departments',
    defaults: ['General Fund', 'Clergy', 'Maintenance', 'Music', 'Education', 'Outreach'] },
  { key: 'church_bgt_heads_v2', module: 'Budget Management', label: 'Account heads',
    defaults: ['Paster payment', 'Electricity', "Pastor's Basic Salary", 'D.A.', 'City allowance', 'Local conveyance'] },
  { key: 'erp_bill_cats', module: 'Statutory Payments', label: 'Bill categories',
    defaults: ['Electricity (EB)', 'Water Bill', 'Internet', 'Telephone', 'Property Tax', 'AMC Contract'] },
  { key: 'erp_bill_depts', module: 'Statutory Payments', label: 'Bill departments / premises',
    defaults: ['Main Church', 'Parish Hall', 'Pastor House', 'Cemetery', 'Office'] },
  { key: 'erp_bill_provs', module: 'Statutory Payments', label: 'Service providers',
    defaults: ['TNEB', 'BSNL', 'Municipality', 'Panchayat'] },
  { key: 'erp_bill_freqs', module: 'Statutory Payments', label: 'Payment frequencies',
    defaults: ['Monthly', 'Bi-Monthly', 'Quarterly', 'Half-Yearly', 'Yearly'] },

  // Ministries
  { key: 'church_yf_fellowships', module: 'Youth Fellowship', label: 'Fellowships',
    defaults: ['Youth Fellowship', "Women's Fellowship", "Men's Fellowship"] },
  { key: 'church_yf_edu', module: 'Youth Fellowship', label: 'Education levels',
    defaults: ['High School', 'Undergraduate', 'Postgraduate', 'Diploma/Vocational'] },
  { key: 'church_yf_emp', module: 'Youth Fellowship', label: 'Employment statuses',
    defaults: ['Student', 'Employed', 'Self-Employed', 'Job Seeking'] },
  { key: 'church_yf_prog_types', module: 'Youth Fellowship', label: 'Programme types',
    defaults: ['Bible Study', 'Worship Night', 'Community Outreach', 'Retreat/Camp', 'Sports/Recreation'] },
  { key: 'church_chr_voice', module: 'Choir Management', label: 'Voice types',
    defaults: ['Soprano', 'Alto', 'Tenor', 'Bass', 'Instrumentalist'] },
  { key: 'church_chr_roles', module: 'Choir Management', label: 'Choir roles',
    defaults: ['Choir Member', 'Choir Master', 'Organist/Keyboardist', 'Secretary', 'Treasurer'] },
  { key: 'church_chr_events', module: 'Choir Management', label: 'Choir event types',
    defaults: ['Weekly Practice', 'Sunday Worship', 'Holy Communion', 'Wedding', 'Funeral', 'Special Convention'] },
  { key: 'church_chr_asscat', module: 'Choir Management', label: 'Choir asset categories',
    defaults: ['Song Library / Hymn', 'Musical Instrument', 'Choir Uniform / Robe', 'Sound Equipment'] },
  { key: 'erp_miss_fields', module: 'Missionaries', label: 'Mission fields',
    defaults: ['Tribal Area', 'North India', 'City Mission'] },
  { key: 'erp_miss_orgs', module: 'Missionaries', label: 'Mission organisations',
    defaults: ['FMPB', 'IMS', 'NMS', 'Diocesan Mission'] },
  { key: 'erp_miss_types', module: 'Missionaries', label: 'Ministry types',
    defaults: ['Evangelism', 'Church Planting', 'Medical Mission', 'Social Service', 'Youth Ministry'] },
  { key: 'erp_miss_dioceses', module: 'Missionaries', label: 'Dioceses',
    defaults: ['CSI Kanyakumari', 'CSI Tirunelveli', 'CSI Madras', 'CSI Trichy'] },
  { key: 'erp_states', module: 'Missionaries', label: 'States',
    defaults: ['Tamil Nadu', 'Kerala', 'Karnataka', 'Andhra Pradesh', 'Telangana'] },
  { key: 'erp_districts', module: 'Missionaries', label: 'Districts',
    defaults: ['Kanyakumari', 'Chennai', 'Madurai', 'Trivandrum', 'Bangalore'] },

  // Registers
  { key: 'church_cem_sections', module: 'Cemetery', label: 'Cemetery sections',
    defaults: ['Block A', 'Block B', 'East Wing', 'West Garden'] },
  { key: 'church_cem_task_types', module: 'Cemetery', label: 'Maintenance task types',
    defaults: ['Grass Cutting', 'Fence Repair', 'Pathway Repair', 'Tree Trimming', 'Cleaning', 'Drainage'] },

  // Administration
  { key: 'church_ast_cat', module: 'Asset Management', label: 'Asset categories',
    defaults: ['Furniture', 'Musical Instruments', 'Audio & Video', 'Computers & IT', 'Vehicles', 'Real Estate/Building'] },
  { key: 'church_ast_loc', module: 'Asset Management', label: 'Asset locations',
    defaults: ['Main Church', 'Parish Hall', 'Pastor Office', 'Choir Room', 'Store Room'] },
  { key: 'church_ast_dept', module: 'Asset Management', label: 'Asset departments',
    defaults: ['General Admin', 'Choir', 'Sunday School', 'Youth Fellowship', 'Media Team'] },
  { key: 'church_mtg_types', module: 'Meeting Management', label: 'Meeting types',
    defaults: ['Church Council', 'Pastorate Committee', 'Finance Committee', 'Property Committee', 'Special General Body'] },
  { key: 'church_mtg_comm', module: 'Meeting Management', label: 'Committees',
    defaults: ['Core Council', 'Choir Committee', 'Sunday School', 'Youth Fellowship', 'Women Fellowship'] },
  { key: 'church_mtg_assignees', module: 'Meeting Management', label: 'Action-item assignees',
    defaults: ['Pastor', 'Church Secretary', 'Treasurer', 'Property Committee', 'Choir Master'] },
  { key: 'church_ph_desig', module: 'Pastor Turnover', label: 'Pastor designations',
    defaults: ['Pastor', 'Assistant Pastor', 'Presbyter', 'Evangelist'] },
  { key: 'church_cm_roles', module: 'Committee History', label: 'Committee roles',
    defaults: ['Secretary', 'Treasurer', 'Church Warden', 'Joint Secretary',
      'Internal Auditor', 'Committee Member', 'Diocesan Representative'] }
  // System user roles are managed under Settings › Users › Roles (SystemRole).
];

export const OPTION_LIST_BY_KEY: Record<string, OptionListDefinition> = OPTION_LISTS.reduce(
  (acc, def) => ({ ...acc, [def.key]: def }),
  {} as Record<string, OptionListDefinition>
);

/** The starting values for a list, or [] for a key the registry does not know. */
export const optionListDefaults = (key: string): readonly string[] =>
  OPTION_LIST_BY_KEY[key]?.defaults ?? [];

/** ChurchConfig.optionLists decoded: key -> values. Malformed entries are dropped. */
export const parseOptionLists = (raw?: string | null): Record<string, string[]> => {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed)
        .filter(([, v]) => Array.isArray(v))
        .map(([k, v]) => [k, (v as unknown[]).filter((x) => typeof x === 'string') as string[]])
    );
  } catch {
    return {};
  }
};
